// ═══════════════════════════════════════════════════════════════
// VISTA: Procesamiento de Nómina
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader,
  Lock,
  Download,
  FileText,
  Clock,
  ChevronDown,
  ChevronUp,
  Users,
} from 'lucide-react';
import { usePayroll } from '../../../hooks/usePayroll';
import { getPayrollDetails, checkPendingIncomes } from '../../../api/payroll';

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

import Pagination from '../../common/Pagination';

// ── Tabla de empleados procesados ─────────────────────────────
function EmployeeResultTable({ records }) {
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 12;

  const totalPages = Math.ceil(records.length / PER_PAGE);
  const start      = (currentPage - 1) * PER_PAGE;
  const current    = records.slice(start, start + PER_PAGE);

  if (records.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
        <Users size={36} className="text-gray-200 mx-auto mb-3" />
        <p className="text-sm text-gray-400">No hay registros de empleados</p>
      </div>
    );
  }

  const avatarColors = [
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-amber-100 text-amber-700',
    'bg-orange-100 text-orange-700',
    'bg-indigo-100 text-indigo-700',
    'bg-slate-100 text-slate-600',
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr className="border-b border-gray-200">
              {['EMPLEADO', 'SALARIO BASE', 'INGRESOS VAR.', 'BRUTO', 'TSS', 'ISR', 'OTRAS DED.', 'NETO'].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {current.map((r) => {
              const name     = r.employee_name || `${r.nombre || ''} ${r.apellido || ''}`.trim();
              const initials = name.split(' ').map(n => n[0] || '').join('').slice(0, 2).toUpperCase();
              const colorIdx = (name.charCodeAt(0) || 0) % avatarColors.length;

              return (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  {/* Empleado */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColors[colorIdx]}`}>
                        {initials}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{name}</p>
                        {r.employee_code && (
                          <p className="text-xs font-mono text-blue-500">{r.employee_code}</p>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3 tabular-nums text-sm text-gray-700">
                    {fmt(r.salario_base)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-sm text-gray-600">
                    {fmt(r.total_ingresos_variables)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-sm font-semibold text-gray-900">
                    {fmt(r.total_bruto)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-sm text-red-600">
                    {fmt(r.tss_empleado)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-sm text-red-600">
                    {fmt(r.isr_monto)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-sm text-red-600">
                    {fmt(r.total_otras_deducciones)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-sm font-bold text-blue-700">
                    {fmt(r.neto_a_pagar)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        total={records.length}
        pageSize={PER_PAGE}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}

// ── Vista principal ────────────────────────────────────────────
export default function PayrollProcessingView({ payroll, onBack, onComplete }) {
  const { processPayroll, closePayroll, exportToExcel, loading } = usePayroll();

  const [processing,      setProcessing]      = useState(false);
  const [processed,       setProcessed]       = useState(false);
  const [processResult,   setProcessResult]   = useState(null);
  const [employeeRecords, setEmployeeRecords] = useState([]);
  const [currentStep,     setCurrentStep]     = useState(null);
  const [pendingIncomes,  setPendingIncomes]  = useState(null);
  const [showPending,     setShowPending]     = useState(false);

  useEffect(() => {
    checkPendingIncomes(payroll.id)
      .then(res => setPendingIncomes(res.pending || []))
      .catch(() => setPendingIncomes([]));
  }, [payroll.id]); // eslint-disable-line

  // Si la nómina ya está procesada, cargar registros directamente
  useEffect(() => {
    if (payroll.estado === 'processed' || payroll.estado === 'closed') {
      setProcessed(true);
      getPayrollDetails(payroll.id)
        .then(details => {
          setEmployeeRecords(details.records || details.employees || []);
          const recs = details.records || details.employees || [];
          setProcessResult(details.summary || {
            totalEmpleados:   recs.length,
            totalBruto:       recs.reduce((s, r) => s + parseFloat(r.total_bruto      || 0), 0),
            totalDeducciones: recs.reduce((s, r) => s + parseFloat(r.total_deducciones || 0), 0),
            totalNeto:        recs.reduce((s, r) => s + parseFloat(r.neto_a_pagar      || 0), 0),
          });
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const steps = [
    { id: 'validating',  label: 'Validando datos'     },
    { id: 'calculating', label: 'Calculando nómina'   },
    { id: 'saving',      label: 'Guardando registros' },
    { id: 'complete',    label: 'Completado'          },
  ];

  const stepIdx = (id) => steps.findIndex(s => s.id === id);

  const processPayrollNow = async () => {
    try {
      setProcessing(true);
      setCurrentStep('validating');

      const details     = await getPayrollDetails(payroll.id);
      const employeeIds = (details.selectedEmployees || []).map(e => e.id);

      if (employeeIds.length === 0) throw new Error('No hay empleados seleccionados en esta nómina');

      await new Promise(r => setTimeout(r, 800));
      setCurrentStep('calculating');
      await new Promise(r => setTimeout(r, 1200));
      setCurrentStep('saving');

      const result = await processPayroll(payroll.id, employeeIds);

      setCurrentStep('complete');
      const summary = result.summary || result;
      setProcessResult(summary);

      // Cargar registros individuales para la tabla
      const updated = await getPayrollDetails(payroll.id);
      setEmployeeRecords(updated.records || updated.employees || []);

      setProcessed(true);

    } catch (error) {
      console.error('Error processing payroll:', error);
      const msg = error.response?.data?.error || error.message;
      alert('Error al procesar nómina: ' + msg);
      setProcessing(false);
    }
  };

  const handleClose = async () => {
    if (!confirm('¿Cerrar esta nómina? No podrás realizar cambios después.')) return;
    try {
      await closePayroll(payroll.id, localStorage.getItem('user_id'));
      alert('Nómina cerrada exitosamente');
      onComplete();
    } catch (error) {
      alert('Error al cerrar nómina: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleExport = async () => {
    try {
      await exportToExcel(payroll.id);
    } catch (error) {
      alert('Error al exportar: ' + error.message);
    }
  };

  return (
    <div className="space-y-4">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={16} />
          Volver
        </button>

        <div className="flex items-center gap-2">
          {processed && (
            <>
              <button
                onClick={handleExport}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download size={15} />
                Exportar Excel
              </button>
              {payroll.estado !== 'closed' && (
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  <Lock size={15} />
                  Cerrar Nómina
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Título + meta ──────────────────────────────────────── */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Procesar Nómina</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          {payroll.periodo} • {payroll.quincena} • {payroll.tipo}
        </p>
      </div>

      {/* ── Wizard (solo mientras no está procesada) ─────────── */}
      {!processed && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-5">
            {processing ? 'Procesando…' : '¿Listo para procesar?'}
          </h2>

          {/* Steps */}
          <div className="space-y-2 mb-6">
            {steps.map((step, index) => {
              const active   = processing && currentStep === step.id;
              const complete = processing && stepIdx(currentStep) > index;
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                    active
                      ? 'bg-blue-50 border-blue-200'
                      : complete
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-100'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    active ? 'bg-blue-500' : complete ? 'bg-green-500' : 'bg-gray-200'
                  }`}>
                    {active   && <Loader size={14} className="text-white animate-spin" />}
                    {complete && <CheckCircle size={14} className="text-white" />}
                    {!active && !complete && <span className="text-[10px] font-bold text-gray-400">{index + 1}</span>}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${active || complete ? 'text-gray-800' : 'text-gray-400'}`}>
                      {step.label}
                    </p>
                    {active   && <p className="text-xs text-blue-500">En progreso…</p>}
                    {complete && <p className="text-xs text-green-600">Completado</p>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Ingresos pendientes */}
          {!processing && pendingIncomes === null && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-100 rounded-lg text-xs text-gray-400 mb-4">
              <Loader size={13} className="animate-spin" />
              Verificando ingresos variables pendientes…
            </div>
          )}

          {!processing && pendingIncomes && pendingIncomes.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-lg overflow-hidden mb-4">
              <div className="flex items-start gap-3 p-4">
                <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-800">
                    {pendingIncomes.length} ingreso{pendingIncomes.length > 1 ? 's' : ''} variable{pendingIncomes.length > 1 ? 's' : ''} pendiente{pendingIncomes.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    No se incluirán en el cálculo. Apruébalos en <strong>Ingresos Variables</strong> antes de procesar.
                  </p>
                </div>
                <button
                  onClick={() => setShowPending(v => !v)}
                  className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 flex-shrink-0"
                >
                  {showPending ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  {showPending ? 'Ocultar' : 'Ver'}
                </button>
              </div>

              {showPending && (
                <div className="border-t border-amber-200 overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-amber-100 text-amber-700">
                        <th className="px-3 py-2 text-left font-semibold">Código</th>
                        <th className="px-3 py-2 text-left font-semibold">Empleado</th>
                        <th className="px-3 py-2 text-left font-semibold">Quincena</th>
                        <th className="px-3 py-2 text-right font-semibold">Comisiones</th>
                        <th className="px-3 py-2 text-right font-semibold">Incentivos</th>
                        <th className="px-3 py-2 text-right font-semibold">Otros</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingIncomes.map((r, i) => (
                        <tr key={r.id} className={i % 2 === 0 ? 'bg-white' : 'bg-amber-50/40'}>
                          <td className="px-3 py-1.5 text-gray-500">{r.codigo}</td>
                          <td className="px-3 py-1.5 font-medium text-gray-800">{r.empleado}</td>
                          <td className="px-3 py-1.5 text-gray-500">{r.quincena}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.comisiones)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.incentivos)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">
                            {fmt(parseFloat(r.horas_extras || 0) + parseFloat(r.bonificaciones || 0) + parseFloat(r.otros_ingresos || 0) + parseFloat(r.vacaciones || 0))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="px-4 pb-3 flex items-center gap-1.5">
                <Clock size={12} className="text-amber-500" />
                <span className="text-xs text-amber-600">Ve a <strong>Ingresos Variables</strong> para aprobar antes de continuar.</span>
              </div>
            </div>
          )}

          {/* Botón */}
          {!processing && pendingIncomes !== null && (
            <div className="flex justify-center">
              <button
                onClick={processPayrollNow}
                className="px-8 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
              >
                {pendingIncomes.length > 0 ? 'Procesar de todas formas' : 'Iniciar Procesamiento'}
              </button>
            </div>
          )}
          {!processing && pendingIncomes && pendingIncomes.length > 0 && (
            <p className="text-center text-xs text-amber-600 mt-2">
              Los {pendingIncomes.length} ingresos pendientes serán excluidos del cálculo
            </p>
          )}
        </div>
      )}

      {/* ── Resultados ─────────────────────────────────────────── */}
      {processed && processResult && (
        <>
          {/* Banner éxito */}
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
            <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">Nómina procesada exitosamente</p>
              <p className="text-xs text-green-600 mt-0.5">
                {processResult.totalEmpleados || employeeRecords.length} empleados procesados
              </p>
            </div>
            {payroll.estado === 'closed' && (
              <span className="ml-auto px-2.5 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                Cerrada
              </span>
            )}
          </div>

          {/* Resumen */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Total Bruto</p>
              <p className="text-xl font-bold text-gray-900">RD$ {fmt(processResult.totalBruto)}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Total Deducciones</p>
              <p className="text-xl font-bold text-red-600">RD$ {fmt(processResult.totalDeducciones)}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4 border-blue-200">
              <p className="text-xs text-blue-500 mb-1">Total Neto</p>
              <p className="text-xl font-bold text-blue-700">RD$ {fmt(processResult.totalNeto)}</p>
            </div>
          </div>

          {/* Tabla de empleados */}
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">
              Detalle por empleado
              <span className="ml-2 text-xs font-normal text-gray-400">
                {employeeRecords.length} registros
              </span>
            </p>
          </div>

          <EmployeeResultTable records={employeeRecords} />

          {/* Aviso cierre */}
          {payroll.estado !== 'closed' && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
              <strong>Importante:</strong> Una vez cerrada la nómina no podrás realizar cambios. Revisa los datos antes de cerrar.
            </div>
          )}
        </>
      )}
    </div>
  );
}
